/**
 * The language, as it travels on the wire.
 *
 * Two different headers, because two different kinds of service consume them:
 *
 *   - `Accept-Language` on GraphQL, because the CRM backend resolves a locale
 *     per request through standard content negotiation (its precedence is
 *     explicit argument > user preference > workspace default > Accept-Language
 *     > English, so this header is what decides for a user who has never picked
 *     a language).
 *   - `X-Locale` on the agent-ai HTTP clients, which take a flat code the way
 *     they already take a flat `X-Timezone`.
 *
 * Both are read at REQUEST time, never captured at module load: the user can
 * change language mid-session, and a captured value would pin every later
 * request to whatever was active when the module first ran. This is the same
 * reason `x-workspace-id` reads localStorage per request.
 *
 * Servers that do not read these headers yet ignore them, so sending them is
 * safe ahead of the backends that consume them.
 */
import i18n from "./index";
import { DEFAULT_LANGUAGE, intlTagFor, normalizeLanguage } from "./languages";

/** The active language as a bare code we ship (`fr`), never a regional tag. */
export function currentLanguage(): string {
  return normalizeLanguage(i18n.language) ?? DEFAULT_LANGUAGE;
}

/**
 * An RFC 9110 `Accept-Language` value for the active language.
 *
 * Sends the regional tag first (`fr-FR`) so a server with regional catalogs can
 * use it, then the base code, then English as the last resort — which is what
 * lets a server that ships no French at all still answer in something rather
 * than guessing. English itself stays a no-op path: `en` produces
 * `en-US,en;q=0.9`, which is what an English browser would send anyway.
 */
export function acceptLanguageValue(code: string = currentLanguage()): string {
  const base = normalizeLanguage(code) ?? DEFAULT_LANGUAGE;
  const tag = intlTagFor(base);
  if (base === DEFAULT_LANGUAGE) return `${tag},${base};q=0.9`;
  return `${tag},${base};q=0.9,${DEFAULT_LANGUAGE};q=0.8`;
}

/** `Accept-Language`, ready to spread into a headers object. */
export function acceptLanguageHeader(): Record<string, string> {
  return { "Accept-Language": acceptLanguageValue() };
}

/**
 * `X-Locale`, ready to spread into an agent-ai fetch's headers alongside
 * `X-Timezone` and `x-workspace-id`.
 */
export function localeHeader(): Record<string, string> {
  return { "X-Locale": currentLanguage() };
}
